import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasTitleText, readPresentation, titlePlaceholder } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionGliederung: Lesson = {
  id: "gliederung",
  title: "Die Gliederungsansicht",
  summary: "Der Röntgenblick auf eine Präsentation: Was hier fehlt, fehlt in der Struktur.",
  durationMinutes: 9,
  learningGoals: [
    "Die Gliederungsansicht als Prüfwerkzeug einsetzen",
    "Jeder Folie einen aussagekräftigen Titel geben",
    "Verstehen, wofür PowerPoint die Folientitel intern verwendet",
  ],
  steps: [
    {
      id: "was-die-gliederung-zeigt",
      title: "Was die Gliederungsansicht zeigt",
      explanation: [
        "Unter „Ansicht“ → „Gliederungsansicht“ verschwindet links die Miniaturleiste " +
          "und macht einer reinen Textspalte Platz. Dort steht die Präsentation als " +
          "Textgliederung: pro Folie der Titel, darunter eingerückt der Inhalt der " +
          "Textplatzhalter.",
        "Der entscheidende Punkt ist, was dort nicht steht. Freie Textfelder, " +
          "WordArt-Objekte, Text in Formen und Text in Bildern erscheinen nicht. Die " +
          "Gliederungsansicht zeigt ausschließlich Inhalte aus Titel- und " +
          "Textplatzhaltern.",
        "Damit ist sie das ehrlichste Prüfwerkzeug, das PowerPoint mitbringt. Wenn eine " +
          "Folie in der Gliederung leer aussieht, obwohl sie voller Text ist, dann steckt " +
          "dieser Text in freien Textfeldern – und ist für PowerPoint unsichtbar. Diese " +
          "Prüfung dauert zehn Sekunden und ersetzt jede Diskussion darüber, ob eine " +
          "Präsentation sauber aufgebaut ist.",
      ],
      keyPoints: [
        "Ansicht → Gliederungsansicht.",
        "Sichtbar sind nur Titel- und Textplatzhalter, sonst nichts.",
        "Leere Zeile in der Gliederung = Folie ohne Titel oder Text in freien Feldern.",
      ],
    },
    {
      id: "jede-folie-einen-titel",
      title: "Jede Folie braucht einen Titel",
      explanation: [
        "Ein fehlender Folientitel ist der häufigste Strukturfehler überhaupt – und " +
          "einer, der lange folgenlos bleibt. Erst wenn eine Präsentation wächst, wird er " +
          "teuer.",
        "PowerPoint verwendet den Titel an überraschend vielen Stellen: in der " +
          "Gliederungsansicht, in der Foliensortierung, in der Sprungliste beim " +
          "Präsentieren, in Hyperlinks auf Folien, beim Zusammenführen mehrerer " +
          "Präsentationen, beim Export nach Word – und Screenreader lesen ihn als " +
          "Erstes vor, damit blinde Zuhörende wissen, wo sie sich befinden.",
        "Soll der Titel auf der Folie nicht sichtbar sein, weil sie zum Beispiel nur ein " +
          "ganzseitiges Bild zeigt, ist das kein Grund, ihn wegzulassen. Der richtige Weg " +
          "führt über „Start“ → „Anordnen“ → „Auswahlbereich“: Dort lässt sich der " +
          "Titelplatzhalter über das Augensymbol ausblenden. Er bleibt vorhanden, wird " +
          "gelesen und exportiert, ist aber auf der Folie unsichtbar.",
      ],
      keyPoints: [
        "Start → Anordnen → Auswahlbereich blendet einen Titel aus, ohne ihn zu löschen.",
        "Screenreader lesen den Folientitel als Erstes vor.",
        "Ein leerer Titelplatzhalter zählt nicht als Titel.",
      ],
      task: {
        instruction: "Gib jeder Folie deiner Präsentation einen Titel.",
        hint: "In der Gliederungsansicht siehst du sofort, welche Folie noch keinen hat.",
        check: async () => {
          const snapshot = await readPresentation();

          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst Folien an (Strg + M).",
            );
          }
          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Die Platzhalter einer Folie",
              "1.3",
              "Öffne die Gliederungsansicht und sieh nach, ob jede Folie eine Titelzeile hat.",
            );
          }

          const ohneTitelplatzhalter = snapshot.slides.filter(
            (folie) => titlePlaceholder(folie) === null,
          );
          const leererTitel = snapshot.slides.filter(
            (folie) => titlePlaceholder(folie) !== null && !hasTitleText(folie),
          );

          if (ohneTitelplatzhalter.length === 0 && leererTitel.length === 0) {
            return pass(
              `Alle ${snapshot.slides.length} Folien haben einen Titel – in der Gliederung ist keine Zeile leer.`,
            );
          }

          const nummern = (folien: typeof snapshot.slides) =>
            folien.map((folie) => folie.index + 1).join(", ");

          return fromCriteria(
            [
              criterion(
                ohneTitelplatzhalter.length === 0
                  ? "Jede Folie hat einen Titelplatzhalter"
                  : `Ohne Titelplatzhalter: Folie ${nummern(ohneTitelplatzhalter)}`,
                ohneTitelplatzhalter.length === 0,
              ),
              criterion(
                leererTitel.length === 0
                  ? "Kein Titelplatzhalter ist leer"
                  : `Titelplatzhalter leer auf Folie ${nummern(leererTitel)}`,
                leererTitel.length === 0,
              ),
            ],
            {
              success: "Alle Folien haben einen Titel.",
              failure: "Auf mindestens einer Folie fehlt der Titel.",
              hint:
                ohneTitelplatzhalter.length > 0
                  ? "Folien ohne Titelplatzhalter bekommen über „Start“ → „Layout“ ein Layout mit Titel. Alternativ: „Start“ → „Zurücksetzen“."
                  : "Der Platzhalter ist vorhanden, aber leer. Klick hinein und tippe den Titel direkt dort ein.",
            },
          );
        },
      },
    },
    {
      id: "gliederung-arbeiten",
      title: "In der Gliederung arbeiten",
      explanation: [
        "Die Gliederungsansicht ist nicht nur zum Anschauen da. Man kann direkt darin " +
          "tippen, und das ist beim Aufbau einer neuen Präsentation oft der schnellste " +
          "Weg: Erst die Titel aller Folien untereinander schreiben, dann mit der " +
          "Tabulatortaste die Stichpunkte darunter einrücken, dann mit Umschalt + Tab " +
          "wieder auf Titelebene zurück – die nächste Folie entsteht automatisch.",
        "So entsteht die Struktur vor der Gestaltung. Das ist genau die Reihenfolge, die " +
          "erfahrene Vortragende empfehlen: erst wissen, was man sagen will, dann " +
          "gestalten.",
        "Umgekehrt lässt sich eine bestehende Gliederung aus Word übernehmen. Ein " +
          "Word-Dokument, dessen Überschriften mit den Formatvorlagen „Überschrift 1“ " +
          "und „Überschrift 2“ ausgezeichnet sind, wird über „Start“ → „Neue Folie“ → " +
          "„Folien aus Gliederung“ zu einer fertigen Foliengliederung: Jede Überschrift 1 " +
          "wird ein Folientitel, jede Überschrift 2 ein Stichpunkt. Das funktioniert nur, " +
          "wenn im Word-Dokument wirklich Formatvorlagen verwendet wurden – der rote " +
          "Faden des Word-Kurses zahlt hier direkt aus.",
      ],
      keyPoints: [
        "Tab rückt in der Gliederung eine Ebene tiefer, Umschalt + Tab eine Ebene höher.",
        "Start → Neue Folie → Folien aus Gliederung importiert ein Word-Dokument.",
        "Der Import funktioniert nur mit echten Word-Überschriftenvorlagen.",
      ],
      task: {
        instruction:
          "Sieh deine Gliederung durch: Keine zwei Folien sollen denselben Titel tragen.",
        hint: "Doppelte Titel entstehen fast immer beim Duplizieren mit Strg + D.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Der Text der Titelplatzhalter",
              "1.3",
              "Lies die Titel in der Gliederungsansicht durch – kommt einer doppelt vor?",
            );
          }

          const titel = snapshot.slides
            .map((folie) => ({
              nummer: folie.index + 1,
              text: titlePlaceholder(folie)?.paragraphs[0]?.trim() ?? "",
            }))
            .filter((eintrag) => eintrag.text.length > 0);

          if (titel.length < 2) {
            return fail(
              "Es gibt noch weniger als zwei Folien mit Titel.",
              "Lege weitere Folien an und gib ihnen einen Titel.",
            );
          }

          const gesehen = new Map<string, number>();
          for (const eintrag of titel) {
            const schluessel = eintrag.text.toLocaleLowerCase("de");
            const erste = gesehen.get(schluessel);
            if (erste !== undefined) {
              return fail(
                `Folie ${erste} und Folie ${eintrag.nummer} tragen beide den Titel „${eintrag.text}“.`,
                "In der Gliederung sind die beiden dann nicht zu unterscheiden. Ergänze den zweiten Titel um das, was ihn ausmacht.",
              );
            }
            gesehen.set(schluessel, eintrag.nummer);
          }

          return pass(
            `Alle ${titel.length} Folientitel sind unterschiedlich – die Gliederung liest sich als Abfolge.`,
          );
        },
      },
    },
  ],
};
